/**
 * Brand kit + social media SVG templates for Easy Gutachter partners.
 *
 * Every template is a function that returns an SVG string with brand
 * colours and the partner-supplied display name. The SVG contains an
 * `<image href="/easy-gutachter-logo.png"/>` reference which gets inlined
 * (base64) before rasterising — see lib/downloadHelpers.js.
 *
 * Brand-Farben:
 *  cyan-600  #0891B2
 *  emerald-500 #10B981
 *  amber-500 #F59E0B
 *  rose-500 #F43F5E
 *  zinc-900 #18181B
 */

const escape = (s) => String(s || "")
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&apos;");

// ----- Brand colours (also exposed as palette) -----
export const BRAND_COLORS = [
  { name: "Cyan",     hex: "#06B6D4", role: "Primär",   className: "bg-cyan-500" },
  { name: "Emerald",  hex: "#10B981", role: "Akzent",   className: "bg-emerald-500" },
  { name: "Amber",    hex: "#F59E0B", role: "Highlight", className: "bg-amber-500" },
  { name: "Rose",     hex: "#F43F5E", role: "Warnung",  className: "bg-rose-500" },
  { name: "Anthrazit",hex: "#18181B", role: "Text",     className: "bg-zinc-900" },
  { name: "Hellgrau", hex: "#F4F4F5", role: "Flächen",  className: "bg-zinc-100 border" },
];

// ----- Logo SVG (vector copy of the Easy Gutachter wordmark) -----
// Used as the SVG download for partners who need a vector logo. The PNG
// download is the existing /easy-gutachter-logo.png file.
export const LOGO_SVG_VECTOR = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 320">
  <defs>
    <linearGradient id="g1" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#06B6D4"/>
      <stop offset="100%" stop-color="#10B981"/>
    </linearGradient>
  </defs>
  <circle cx="160" cy="160" r="148" fill="white" stroke="url(#g1)" stroke-width="10"/>
  <circle cx="160" cy="160" r="120" fill="url(#g1)" opacity="0.08"/>
  <text x="160" y="142" text-anchor="middle" font-family="-apple-system, system-ui, 'Segoe UI', Roboto, sans-serif"
        font-weight="800" font-size="40" fill="url(#g1)" letter-spacing="-1">EASY</text>
  <text x="160" y="190" text-anchor="middle" font-family="-apple-system, system-ui, 'Segoe UI', Roboto, sans-serif"
        font-weight="800" font-size="40" fill="url(#g1)" letter-spacing="-1">GUTACHTER</text>
  <path d="M 110 220 L 145 240 L 210 200" fill="none" stroke="url(#g1)" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/>
  <line x1="80"  y1="252" x2="240" y2="252" stroke="url(#g1)" stroke-width="3" stroke-linecap="round" opacity="0.6"/>
  <line x1="105" y1="262" x2="215" y2="262" stroke="url(#g1)" stroke-width="3" stroke-linecap="round" opacity="0.4"/>
</svg>`;

// White logo (for dark backgrounds): same shape, single colour.
export const LOGO_SVG_WHITE = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 320">
  <circle cx="160" cy="160" r="148" fill="none" stroke="#fff" stroke-width="10"/>
  <text x="160" y="142" text-anchor="middle" font-family="-apple-system, system-ui, 'Segoe UI', Roboto, sans-serif"
        font-weight="800" font-size="40" fill="#fff" letter-spacing="-1">EASY</text>
  <text x="160" y="190" text-anchor="middle" font-family="-apple-system, system-ui, 'Segoe UI', Roboto, sans-serif"
        font-weight="800" font-size="40" fill="#fff" letter-spacing="-1">GUTACHTER</text>
  <path d="M 110 220 L 145 240 L 210 200" fill="none" stroke="#fff" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/>
  <line x1="80"  y1="252" x2="240" y2="252" stroke="#fff" stroke-width="3" stroke-linecap="round" opacity="0.6"/>
  <line x1="105" y1="262" x2="215" y2="262" stroke="#fff" stroke-width="3" stroke-linecap="round" opacity="0.4"/>
</svg>`;

// ----- Social media post templates -----
// Each template is a `{ id, label, format, w, h, build(name, headline, subline) }`

export const TEMPLATES = [
  {
    id: "ig-post-classic",
    label: "Instagram · Klassisch",
    format: "Instagram Post",
    w: 1080, h: 1080,
    build: (name, headline, sub) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1080 1080">
      <defs>
        <linearGradient id="bg1" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#06B6D4"/>
          <stop offset="100%" stop-color="#10B981"/>
        </linearGradient>
      </defs>
      <rect width="1080" height="1080" fill="url(#bg1)"/>
      <circle cx="900" cy="180" r="320" fill="#fff" opacity="0.06"/>
      <circle cx="100" cy="980" r="220" fill="#fff" opacity="0.06"/>
      <image href="/easy-gutachter-logo.png" x="60" y="60" width="120" height="120"/>
      <text x="60" y="430" font-family="-apple-system, system-ui, 'Segoe UI', sans-serif" font-weight="900" font-size="84" fill="white" letter-spacing="-2">${escape(headline)}</text>
      <text x="60" y="540" font-family="-apple-system, system-ui, 'Segoe UI', sans-serif" font-weight="900" font-size="84" fill="white" letter-spacing="-2" opacity="0.95">${escape(sub)}</text>
      <line x1="60" y1="630" x2="200" y2="630" stroke="#fff" stroke-width="6" opacity="0.7"/>
      <text x="60" y="940" font-family="-apple-system, system-ui, 'Segoe UI', sans-serif" font-weight="700" font-size="32" fill="white" opacity="0.9">${escape(name)}</text>
      <text x="60" y="990" font-family="-apple-system, system-ui, 'Segoe UI', sans-serif" font-weight="500" font-size="26" fill="white" opacity="0.7">Kfz-Sachverständiger · Easy Gutachter Partner</text>
    </svg>`,
  },
  {
    id: "ig-post-quote",
    label: "Instagram · Zitat",
    format: "Instagram Post",
    w: 1080, h: 1080,
    build: (name, headline, sub) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1080 1080">
      <rect width="1080" height="1080" fill="#0F172A"/>
      <circle cx="540" cy="540" r="800" fill="none" stroke="#10B981" stroke-width="2" opacity="0.1"/>
      <circle cx="540" cy="540" r="650" fill="none" stroke="#06B6D4" stroke-width="2" opacity="0.15"/>
      <text x="540" y="280" text-anchor="middle" font-family="Georgia, serif" font-weight="700" font-size="200" fill="#10B981" opacity="0.5">"</text>
      <text x="540" y="500" text-anchor="middle" font-family="-apple-system, system-ui, sans-serif" font-weight="800" font-size="64" fill="#fff" letter-spacing="-1">${escape(headline)}</text>
      <text x="540" y="600" text-anchor="middle" font-family="-apple-system, system-ui, sans-serif" font-weight="500" font-size="40" fill="#cbd5e1" letter-spacing="-0.5">${escape(sub)}</text>
      <image href="/easy-gutachter-logo.png" x="490" y="800" width="100" height="100"/>
      <text x="540" y="940" text-anchor="middle" font-family="-apple-system, system-ui, sans-serif" font-weight="600" font-size="26" fill="#fff" opacity="0.8">${escape(name)}</text>
      <text x="540" y="985" text-anchor="middle" font-family="-apple-system, system-ui, sans-serif" font-weight="500" font-size="22" fill="#94a3b8">Easy Gutachter Partner</text>
    </svg>`,
  },
  {
    id: "ig-post-tip",
    label: "Instagram · Tipp",
    format: "Instagram Post",
    w: 1080, h: 1080,
    build: (name, headline, sub) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1080 1080">
      <rect width="1080" height="1080" fill="#fff"/>
      <rect width="1080" height="180" fill="#0891B2"/>
      <text x="60" y="115" font-family="-apple-system, system-ui, sans-serif" font-weight="800" font-size="50" fill="#fff" letter-spacing="-1">💡 Profi-Tipp</text>
      <text x="60" y="155" font-family="-apple-system, system-ui, sans-serif" font-weight="500" font-size="24" fill="#fff" opacity="0.9">vom Kfz-Sachverständigen</text>
      <text x="60" y="380" font-family="-apple-system, system-ui, sans-serif" font-weight="900" font-size="76" fill="#0F172A" letter-spacing="-2">${escape(headline)}</text>
      <text x="60" y="500" font-family="-apple-system, system-ui, sans-serif" font-weight="500" font-size="38" fill="#475569" letter-spacing="-0.5">${escape(sub)}</text>
      <rect x="60" y="600" width="960" height="2" fill="#e5e7eb"/>
      <rect x="60" y="640" width="160" height="60" rx="8" fill="#0891B2"/>
      <text x="140" y="680" text-anchor="middle" font-family="-apple-system, system-ui, sans-serif" font-weight="800" font-size="28" fill="#fff">SPAREN</text>
      <rect x="240" y="640" width="220" height="60" rx="8" fill="#10B981"/>
      <text x="350" y="680" text-anchor="middle" font-family="-apple-system, system-ui, sans-serif" font-weight="800" font-size="28" fill="#fff">PROFI-WISSEN</text>
      <image href="/easy-gutachter-logo.png" x="60" y="940" width="80" height="80"/>
      <text x="160" y="980" font-family="-apple-system, system-ui, sans-serif" font-weight="700" font-size="28" fill="#0F172A">${escape(name)}</text>
      <text x="160" y="1015" font-family="-apple-system, system-ui, sans-serif" font-weight="500" font-size="22" fill="#64748b">Easy Gutachter Partner</text>
    </svg>`,
  },
  {
    id: "ig-story",
    label: "Instagram · Story",
    format: "Story 9:16",
    w: 1080, h: 1920,
    build: (name, headline, sub) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1080 1920">
      <defs>
        <linearGradient id="storyBg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#06B6D4"/>
          <stop offset="100%" stop-color="#10B981"/>
        </linearGradient>
      </defs>
      <rect width="1080" height="1920" fill="url(#storyBg)"/>
      <circle cx="900" cy="200" r="380" fill="#fff" opacity="0.06"/>
      <circle cx="180" cy="1700" r="280" fill="#fff" opacity="0.06"/>
      <image href="/easy-gutachter-logo.png" x="80" y="100" width="140" height="140"/>
      <text x="80" y="780" font-family="-apple-system, system-ui, sans-serif" font-weight="900" font-size="120" fill="white" letter-spacing="-3">${escape(headline)}</text>
      <text x="80" y="940" font-family="-apple-system, system-ui, sans-serif" font-weight="500" font-size="56" fill="white" opacity="0.9" letter-spacing="-1">${escape(sub)}</text>
      <rect x="80" y="1100" width="920" height="100" rx="50" fill="#fff"/>
      <text x="540" y="1170" text-anchor="middle" font-family="-apple-system, system-ui, sans-serif" font-weight="800" font-size="44" fill="#0891B2">JETZT KONTAKTIEREN</text>
      <text x="80" y="1750" font-family="-apple-system, system-ui, sans-serif" font-weight="700" font-size="40" fill="white">${escape(name)}</text>
      <text x="80" y="1810" font-family="-apple-system, system-ui, sans-serif" font-weight="500" font-size="32" fill="white" opacity="0.8">Kfz-Sachverständiger</text>
    </svg>`,
  },
  {
    id: "fb-cover",
    label: "Facebook · Cover",
    format: "FB Cover 820×312",
    w: 820, h: 312,
    build: (name, headline, sub) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 820 312">
      <defs>
        <linearGradient id="fbBg" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stop-color="#0F172A"/>
          <stop offset="100%" stop-color="#0891B2"/>
        </linearGradient>
      </defs>
      <rect width="820" height="312" fill="url(#fbBg)"/>
      <image href="/easy-gutachter-logo.png" x="40" y="86" width="140" height="140"/>
      <text x="220" y="140" font-family="-apple-system, system-ui, sans-serif" font-weight="900" font-size="44" fill="#fff" letter-spacing="-1">${escape(name)}</text>
      <text x="220" y="180" font-family="-apple-system, system-ui, sans-serif" font-weight="600" font-size="22" fill="#10B981">${escape(headline)}</text>
      <text x="220" y="216" font-family="-apple-system, system-ui, sans-serif" font-weight="500" font-size="18" fill="#cbd5e1">${escape(sub)}</text>
    </svg>`,
  },
  {
    id: "li-banner",
    label: "LinkedIn · Banner",
    format: "LinkedIn 1584×396",
    w: 1584, h: 396,
    build: (name, headline, sub) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1584 396">
      <defs>
        <linearGradient id="liBg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#06B6D4"/>
          <stop offset="100%" stop-color="#10B981"/>
        </linearGradient>
      </defs>
      <rect width="1584" height="396" fill="url(#liBg)"/>
      <circle cx="1400" cy="100" r="250" fill="#fff" opacity="0.07"/>
      <image href="/easy-gutachter-logo.png" x="120" y="118" width="160" height="160"/>
      <text x="320" y="180" font-family="-apple-system, system-ui, sans-serif" font-weight="900" font-size="56" fill="#fff" letter-spacing="-1">${escape(name)}</text>
      <text x="320" y="230" font-family="-apple-system, system-ui, sans-serif" font-weight="700" font-size="32" fill="#fff" opacity="0.9">${escape(headline)}</text>
      <text x="320" y="278" font-family="-apple-system, system-ui, sans-serif" font-weight="500" font-size="26" fill="#fff" opacity="0.75">${escape(sub)}</text>
    </svg>`,
  },
];

// ----- Marketing copy presets -----
export const MARKETING_TEXTS = [
  {
    id: "about-short",
    title: "Kurzvorstellung (Twitter / Bio)",
    description: "Max. 160 Zeichen — perfekt für Profil-Bios.",
    text: (name) => `Kfz-Sachverständiger ${name}. Schnelle, transparente Gutachten — komplett digital. Easy Gutachter Partner. 🚗⚙️`,
  },
  {
    id: "about-medium",
    title: "Über mich (mittelang)",
    description: "Für Webseite, LinkedIn-Header oder About-Seiten.",
    text: (name) => `Mein Name ist ${name} und ich bin zertifizierter Kfz-Sachverständiger. Mit der Easy Gutachter Plattform erstelle ich digitale Gutachten in Rekordzeit — transparent, mobil und DSGVO-konform. So bekommst du dein Schadensgutachten innerhalb von 24 Stunden, ohne Papierberge und ohne Wartezeit.`,
  },
  {
    id: "about-long",
    title: "Über mich (ausführlich)",
    description: "Für die Über-uns-Seite deiner Website.",
    text: (name) => `Als unabhängiger Kfz-Sachverständiger ${name} dokumentiere und bewerte ich Fahrzeugschäden, Wertminderungen und Wiederbeschaffungswerte für Privatpersonen, Werkstätten, Versicherungen und Anwälte. Dabei setze ich auf die digitale Plattform Easy Gutachter — mit OCR für Fahrzeugscheine, automatisierter Schadenserkennung und vollständig digitalem Workflow inklusive Live-Kamera-Erfassung und elektronischer Unterschrift.\n\nDas heißt für dich: schnellere Auftragsabwicklung, transparente Kommunikation und ein professionelles Gutachten in PDF-Form, sobald die Begutachtung abgeschlossen ist. Mein Ziel: gerichtsfeste Gutachten in maximal 24 Stunden — ohne Kompromisse bei der Qualität.`,
  },
  {
    id: "service-pitch",
    title: "Werbe-Mailing / Anfrage-Antwort",
    description: "Direkt-Mail an Werkstätten, Anwälte oder Versicherungen.",
    text: (name) => `Sehr geehrte Damen und Herren,\n\nmein Name ist ${name}, ich bin zertifizierter Kfz-Sachverständiger und betreue Schadensgutachten in Ihrer Region. Mit dem Easy Gutachter System erstelle ich vollständige Gutachten innerhalb von 24 Stunden — mit allen Bildern, Fahrzeugdaten, Vorschadenbewertung und elektronischer Unterschrift.\n\nGerne stelle ich Ihnen mein Angebot in einem unverbindlichen Gespräch vor. Sie erreichen mich jederzeit per E-Mail oder telefonisch.\n\nMit besten Grüßen\n${name}\nKfz-Sachverständiger · Easy Gutachter Partner`,
  },
  {
    id: "social-launch",
    title: "Launch-Post (Social Media)",
    description: "Wenn du Easy Gutachter neu in deinem Auftritt vorstellst.",
    text: (name) => `🚀 Neu bei mir im Büro: Easy Gutachter — die digitale Plattform für Kfz-Gutachten.\n\nWas das für dich heißt:\n✅ Gutachten in 24 Stunden\n✅ Live-Foto-Erfassung am Schadensort\n✅ Digitaler Fahrzeugschein-Scan\n✅ Automatische Schadensbeschreibung\n✅ Elektronische Unterschrift — keine Papier-Hin-und-Her\n\nFür Termine: einfach melden 📩\n\n— ${name}\n#KfzGutachter #EasyGutachter #Kfzsachverstaendiger`,
  },
  {
    id: "google-business",
    title: "Google-Business-Profil-Beschreibung",
    description: "750 Zeichen für dein Google Maps Profil.",
    text: (name) => `Kfz-Sachverständiger ${name} — unabhängige Gutachten für Schadensbewertung, Wertminderung und Wiederbeschaffungswert. Wir nutzen die Easy Gutachter Plattform für vollständig digitale Gutachten in 24 Stunden: OCR-Scan des Fahrzeugscheins, automatische Schadensbeschreibung, Live-Foto-Dokumentation und elektronische Unterschrift. Termine flexibel — auch vor Ort beim Schadensobjekt. Ideal nach Unfall, Diebstahl, Hagel oder vor Fahrzeugverkauf.`,
  },
];

// ----- Email signature template -----
export const EMAIL_SIGNATURE_HTML = ({ name = "Max Mustermann", role = "Kfz-Sachverständiger", phone = "+49 30 1234567", email = "info@example.de", address = "" }) => `
<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;color:#0F172A;font-size:14px;line-height:1.4">
  <tr>
    <td style="padding-right:14px;border-right:3px solid #10B981;vertical-align:top">
      <img src="https://easy-gutachter.de/logo.png" alt="Easy Gutachter" width="64" height="64" style="display:block;border-radius:8px"/>
    </td>
    <td style="padding-left:14px;vertical-align:top">
      <div style="font-weight:700;font-size:16px;color:#0F172A">${escape(name)}</div>
      <div style="color:#0891B2;font-weight:600;font-size:13px">${escape(role)} · Easy Gutachter Partner</div>
      <div style="margin-top:6px;color:#475569;font-size:13px">
        ${address ? `${escape(address)}<br/>` : ""}
        Tel: <a href="tel:${escape(phone).replace(/[^+\d]/g, "")}" style="color:#475569;text-decoration:none">${escape(phone)}</a><br/>
        E-Mail: <a href="mailto:${escape(email)}" style="color:#10B981;text-decoration:none">${escape(email)}</a>
      </div>
    </td>
  </tr>
</table>`.trim();

export const EMAIL_SIGNATURE_TEXT = ({ name = "Max Mustermann", role = "Kfz-Sachverständiger", phone = "+49 30 1234567", email = "info@example.de", address = "" }) =>
`${name}
${role} · Easy Gutachter Partner
${address ? address + "\n" : ""}Tel: ${phone}
E-Mail: ${email}`;



// ===================================================================
//   Visitenkarte (Business Card) — Standard 85 × 55 mm @ 300 DPI
//   Druckfertig mit 3 mm Beschnitt (Bleed): Gesamtformat 91 × 61 mm
//   @ 300 DPI = 1076 × 722 px. Innenbereich 1004 × 650 px (85 × 55 mm).
// ===================================================================

const BC_BLEED = 36;                                 // 3 mm Beschnitt @ 300 DPI
const BC_INNER_W = 1004;                             // 85 mm
const BC_INNER_H = 650;                              // 55 mm
const BC_W = BC_INNER_W + 2 * BC_BLEED;              // 1076 px (91 mm — Endformat mit Bleed)
const BC_H = BC_INNER_H + 2 * BC_BLEED;              // 722 px (61 mm — Endformat mit Bleed)

/**
 * Build front-side SVG for a business card. Filled with user's data.
 */
const buildBusinessCardFront = ({ name = "Max Mustermann", role = "Kfz-Sachverständiger",
                                  phone = "", mobile = "", email = "", web = "www.easygutachter.de",
                                  address = "" }) => `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${BC_W} ${BC_H}" preserveAspectRatio="xMidYMid meet" style="width:100%;height:100%;display:block">
  <defs>
    <linearGradient id="bcg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#06B6D4"/>
      <stop offset="100%" stop-color="#10B981"/>
    </linearGradient>
  </defs>
  <!-- Bleed-Hintergrund weiß (volles Druckformat 91 × 61 mm) -->
  <rect width="${BC_W}" height="${BC_H}" fill="#ffffff"/>

  <!-- Linker Akzent-Streifen mit CI-Gradient — geht bis zum vollen Rand inkl. Bleed -->
  <rect x="0" y="0" width="${BC_BLEED + 14}" height="${BC_H}" fill="url(#bcg)"/>

  <!-- Inhalt verschoben um 3 mm Bleed (alle inneren Koordinaten beziehen sich auf 85 × 55 mm) -->
  <g transform="translate(${BC_BLEED}, ${BC_BLEED})">
    <!-- Logo-Kachel oben links -->
    <image href="/easy-gutachter-logo.png" x="56" y="56" width="140" height="140" preserveAspectRatio="xMidYMid meet"/>

    <!-- Name + Rolle rechts -->
    <text x="240" y="105" font-family="Arial, Helvetica, sans-serif"
          font-weight="800" font-size="44" fill="#0F172A" letter-spacing="-0.5">${escape(name)}</text>
    <text x="240" y="155" font-family="Arial, Helvetica, sans-serif"
          font-weight="500" font-size="22" fill="#06B6D4" letter-spacing="0.5">${escape(role)}</text>
    <line x1="240" y1="178" x2="340" y2="178" stroke="url(#bcg)" stroke-width="3"/>

    <!-- Kontaktblock unten -->
    <g font-family="Arial, Helvetica, sans-serif" font-size="20" fill="#0F172A">
      ${phone ? `<text x="56" y="280">📞  ${escape(phone)}</text>` : ""}
      ${mobile ? `<text x="56" y="320">📱  ${escape(mobile)}</text>` : ""}
      ${email ? `<text x="56" y="${phone || mobile ? 360 : 280}">✉️  ${escape(email)}</text>` : ""}
      ${web ? `<text x="56" y="${(phone || mobile ? 1 : 0) * 40 + (email ? 1 : 0) * 40 + (phone || mobile ? 360 : 320)}">🌐  ${escape(web)}</text>` : ""}
      ${address ? `<text x="56" y="${BC_INNER_H - 80}" font-size="17" fill="#65718A">📍  ${escape(address)}</text>` : ""}
    </g>

    <!-- Easy-Gutachter-Tagline rechts unten -->
    <text x="${BC_INNER_W - 56}" y="${BC_INNER_H - 56}" text-anchor="end" font-family="Arial, Helvetica, sans-serif"
          font-weight="700" font-size="14" fill="url(#bcg)" letter-spacing="2">
      EASY GUTACHTER · PARTNER
    </text>
  </g>
</svg>`.trim();

/**
 * Build back-side SVG — full-bleed brand color with claim.
 */
const buildBusinessCardBack = () => `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${BC_W} ${BC_H}" preserveAspectRatio="xMidYMid meet" style="width:100%;height:100%;display:block">
  <defs>
    <linearGradient id="bcgb" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#06B6D4"/>
      <stop offset="100%" stop-color="#10B981"/>
    </linearGradient>
    <pattern id="dots" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
      <circle cx="20" cy="20" r="1.4" fill="#ffffff" opacity="0.18"/>
    </pattern>
  </defs>
  <rect width="${BC_W}" height="${BC_H}" fill="url(#bcgb)"/>
  <rect width="${BC_W}" height="${BC_H}" fill="url(#dots)"/>

  <text x="${BC_W / 2}" y="${BC_H / 2 - 30}" text-anchor="middle"
        font-family="Arial, Helvetica, sans-serif" font-weight="800" font-size="56"
        fill="#fff" letter-spacing="0.5">EASY GUTACHTER</text>
  <line x1="${BC_W / 2 - 100}" y1="${BC_H / 2 + 8}" x2="${BC_W / 2 + 100}" y2="${BC_H / 2 + 8}"
        stroke="#fff" stroke-width="3" stroke-linecap="round" opacity="0.85"/>
  <text x="${BC_W / 2}" y="${BC_H / 2 + 60}" text-anchor="middle"
        font-family="Arial, Helvetica, sans-serif" font-weight="500" font-size="26"
        fill="#fff" opacity="0.95">Schadensgutachten · schnell, unabhängig & digital</text>
  <text x="${BC_W / 2}" y="${BC_H - BC_BLEED - 56}" text-anchor="middle"
        font-family="Arial, Helvetica, sans-serif" font-weight="600" font-size="16"
        fill="#fff" opacity="0.85" letter-spacing="3">WWW.EASYGUTACHTER.DE</text>
</svg>`.trim();

export const BUSINESS_CARD_TEMPLATES = [
  { id: "bc-front", label: "Vorderseite", build: buildBusinessCardFront, w: BC_W, h: BC_H },
  { id: "bc-back",  label: "Rückseite",  build: buildBusinessCardBack,  w: BC_W, h: BC_H },
];


// ===================================================================
//   Flyer DIN A6 — 105 × 148 mm @ 300 DPI = 1240 × 1748 px
//   Hochformat. Wir nehmen den Standardtext aus dem User-Brief,
//   personalisiert mit Name, Telefon, E-Mail.
// ===================================================================

const FL_W = 1240;    // 105 mm @ 300 DPI
const FL_H = 1748;    // 148 mm @ 300 DPI

const buildFlyer = ({ name = "Max Mustermann", phone = "", email = "",
                      web = "www.easygutachter.de" }) => `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${FL_W} ${FL_H}" preserveAspectRatio="xMidYMid meet" style="width:100%;height:100%;display:block">
  <defs>
    <linearGradient id="flg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#06B6D4"/>
      <stop offset="100%" stop-color="#10B981"/>
    </linearGradient>
    <linearGradient id="flgv" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#06B6D4"/>
      <stop offset="100%" stop-color="#10B981"/>
    </linearGradient>
  </defs>

  <!-- Hintergrund -->
  <rect width="${FL_W}" height="${FL_H}" fill="#ffffff"/>

  <!-- Header — Brand-Banner -->
  <rect width="${FL_W}" height="430" fill="url(#flg)"/>
  <image href="/easy-gutachter-logo.png" x="80" y="60" width="160" height="160" preserveAspectRatio="xMidYMid meet"/>
  <text x="260" y="125" font-family="Arial, Helvetica, sans-serif" font-weight="600"
        font-size="22" fill="#fff" opacity="0.9" letter-spacing="2">EASY GUTACHTER</text>
  <text x="260" y="170" font-family="Arial, Helvetica, sans-serif" font-weight="500"
        font-size="22" fill="#fff" opacity="0.8">Ihr unabhängiger Kfz-Sachverständiger</text>

  <text x="80" y="290" font-family="Arial, Helvetica, sans-serif" font-weight="800"
        font-size="68" fill="#fff" letter-spacing="-1">UNFALL GEHABT?</text>
  <text x="80" y="362" font-family="Arial, Helvetica, sans-serif" font-weight="800"
        font-size="44" fill="#fff" opacity="0.95">Wir sind für Sie da!</text>

  <!-- Sub-headline -->
  <text x="80" y="500" font-family="Arial, Helvetica, sans-serif" font-weight="500"
        font-size="26" fill="#0F172A">Ihr unabhängiger Kfz-Gutachter — schnell, zuverlässig &amp;</text>
  <text x="80" y="538" font-family="Arial, Helvetica, sans-serif" font-weight="500"
        font-size="26" fill="#0F172A">kostenlos bei Haftpflichtschäden.</text>
  <text x="80" y="586" font-family="Arial, Helvetica, sans-serif" font-style="italic"
        font-size="22" fill="#65718A">Ein Unfall ist schon ärgerlich genug — wir sorgen dafür,</text>
  <text x="80" y="616" font-family="Arial, Helvetica, sans-serif" font-style="italic"
        font-size="22" fill="#65718A">dass Sie zu Ihrem Recht kommen.</text>

  <!-- Leistungen -->
  <text x="80" y="700" font-family="Arial, Helvetica, sans-serif" font-weight="800"
        font-size="28" fill="#06B6D4">UNSERE LEISTUNGEN AUF EINEN BLICK</text>
  ${[
    "Erstellung von unabhängigen Kfz-Gutachten",
    "Beweissicherung nach einem Unfall",
    "Schadensbewertung für PKW, Transporter & mehr",
    "Wertgutachten",
    "Unterstützung bei der Schadenabwicklung",
  ].map((line, i) => `
    <circle cx="98" cy="${752 + i * 44}" r="9" fill="#10B981"/>
    <path d="M ${91} ${752 + i * 44} L ${97} ${758 + i * 44} L ${107} ${747 + i * 44}"
          fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
    <text x="130" y="${760 + i * 44}" font-family="Arial, Helvetica, sans-serif"
          font-size="22" fill="#0F172A">${escape(line)}</text>
  `).join("")}

  <!-- Vorteile -->
  <text x="80" y="1020" font-family="Arial, Helvetica, sans-serif" font-weight="800"
        font-size="28" fill="#06B6D4">IHRE VORTEILE</text>
  ${[
    "Kostenlos bei unverschuldetem Unfall",
    "Schnelle Terminvergabe — oft am selben Tag",
    "Vor-Ort-Service zuhause, Arbeit oder Werkstatt",
    "100 % unabhängig von Versicherungen",
    "Auf Wunsch: Komplette Abwicklung mit Anwalt",
  ].map((line, i) => `
    <circle cx="98" cy="${1072 + i * 44}" r="9" fill="#F59E0B"/>
    <text x="92" y="${1080 + i * 44}" font-family="Arial, Helvetica, sans-serif"
          font-weight="800" font-size="14" fill="#fff">⚡</text>
    <text x="130" y="${1080 + i * 44}" font-family="Arial, Helvetica, sans-serif"
          font-size="22" fill="#0F172A">${escape(line)}</text>
  `).join("")}

  <!-- Regional-Banner -->
  <rect x="80" y="1310" width="${FL_W - 160}" height="80" rx="14" fill="#F1F5F9"/>
  <text x="${FL_W / 2}" y="1352" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="700" font-size="22" fill="#0F172A">📍 Regional für Sie im Einsatz — schnell bei Ihnen vor Ort!</text>

  <!-- CTA Footer -->
  <rect x="0" y="1430" width="${FL_W}" height="${FL_H - 1430}" fill="url(#flg)"/>
  <text x="${FL_W / 2}" y="1490" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="800" font-size="38" fill="#fff" letter-spacing="0.5">JETZT KONTAKT AUFNEHMEN!</text>
  <line x1="${FL_W / 2 - 100}" y1="1510" x2="${FL_W / 2 + 100}" y2="1510" stroke="#fff" stroke-width="3"/>

  <text x="${FL_W / 2}" y="1572" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="700" font-size="32" fill="#fff">${escape(name)}</text>
  ${phone ? `<text x="${FL_W / 2}" y="1622" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="600" font-size="28" fill="#fff">📞 ${escape(phone)}</text>` : ""}
  ${email ? `<text x="${FL_W / 2}" y="${phone ? 1666 : 1622}" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="500" font-size="26" fill="#fff" opacity="0.95">✉️ ${escape(email)}</text>` : ""}
  <text x="${FL_W / 2}" y="${1700 + (phone ? 0 : -44) + (email ? 0 : -44)}" text-anchor="middle"
        font-family="Arial, Helvetica, sans-serif" font-weight="600" font-size="22" fill="#fff" opacity="0.85">
    🌐 ${escape(web)}
  </text>
</svg>`.trim();

export const FLYER_TEMPLATES = [
  { id: "flyer-a6", label: "Flyer DIN A6 (Hochformat)", build: buildFlyer, w: FL_W, h: FL_H },
];


// ===================================================================
//   Türschild / Praxisschild — DIN A4 Hochformat
//   210 × 297 mm @ 300 DPI = 2480 × 3508 px
// ===================================================================

const TS_W = 2480;
const TS_H = 3508;

const buildTuerschild = ({ name = "Max Mustermann", role = "Kfz-Sachverständiger",
                          phone = "", mobile = "", email = "", web = "www.easygutachter.de",
                          address = "", hours = "Mo-Fr  08:00 - 18:00\nSa  nach Vereinbarung" }) => `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${TS_W} ${TS_H}" preserveAspectRatio="xMidYMid meet" style="width:100%;height:100%;display:block">
  <defs>
    <linearGradient id="tsg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#06B6D4"/>
      <stop offset="100%" stop-color="#10B981"/>
    </linearGradient>
    <linearGradient id="tsgv" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#06B6D4"/>
      <stop offset="100%" stop-color="#10B981"/>
    </linearGradient>
    <pattern id="tsdots" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
      <circle cx="30" cy="30" r="2" fill="#ffffff" opacity="0.14"/>
    </pattern>
  </defs>

  <!-- Hintergrund -->
  <rect width="${TS_W}" height="${TS_H}" fill="#ffffff"/>

  <!-- Brand-Header (oberes Drittel) -->
  <rect width="${TS_W}" height="1100" fill="url(#tsg)"/>
  <rect width="${TS_W}" height="1100" fill="url(#tsdots)"/>

  <!-- Logo zentriert -->
  <image href="/easy-gutachter-logo.png" x="${TS_W / 2 - 250}" y="180" width="500" height="500" preserveAspectRatio="xMidYMid meet"/>

  <text x="${TS_W / 2}" y="780" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="600" font-size="50" fill="#fff" opacity="0.92" letter-spacing="6">EASY GUTACHTER · PARTNER</text>

  <text x="${TS_W / 2}" y="900" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="800" font-size="80" fill="#fff" letter-spacing="-1">Schadensgutachten</text>
  <text x="${TS_W / 2}" y="1010" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="500" font-size="50" fill="#fff" opacity="0.92">schnell · unabhängig · digital</text>

  <!-- Name + Rolle (mittlerer Block) -->
  <text x="${TS_W / 2}" y="1380" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="900" font-size="120" fill="#0F172A" letter-spacing="-2">${escape(name)}</text>
  <line x1="${TS_W / 2 - 200}" y1="1430" x2="${TS_W / 2 + 200}" y2="1430"
        stroke="url(#tsg)" stroke-width="6" stroke-linecap="round"/>
  <text x="${TS_W / 2}" y="1530" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="500" font-size="62" fill="#06B6D4" letter-spacing="0.5">${escape(role)}</text>

  <!-- Öffnungszeiten-Card -->
  <rect x="240" y="1700" width="${TS_W - 480}" height="500" rx="32" fill="#F1F5F9" stroke="#E2E8F0" stroke-width="3"/>
  <text x="${TS_W / 2}" y="1810" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="800" font-size="46" fill="#06B6D4" letter-spacing="3">ÖFFNUNGSZEITEN</text>
  ${(hours || "").split("\n").slice(0, 4).map((line, i) => `
    <text x="${TS_W / 2}" y="${1920 + i * 80}" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
          font-weight="500" font-size="46" fill="#0F172A">${escape(line)}</text>
  `).join("")}

  <!-- Kontaktblock -->
  <text x="${TS_W / 2}" y="2380" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="800" font-size="46" fill="#06B6D4" letter-spacing="3">KONTAKT</text>

  <g font-family="Arial, Helvetica, sans-serif" font-size="48" fill="#0F172A" text-anchor="middle">
    ${phone ? `<text x="${TS_W / 2}" y="2490">📞  ${escape(phone)}</text>` : ""}
    ${mobile ? `<text x="${TS_W / 2}" y="${phone ? 2580 : 2490}">📱  ${escape(mobile)}</text>` : ""}
    ${email ? `<text x="${TS_W / 2}" y="${(phone ? 90 : 0) + (mobile ? 90 : 0) + 2490}">✉  ${escape(email)}</text>` : ""}
    <text x="${TS_W / 2}" y="${(phone ? 90 : 0) + (mobile ? 90 : 0) + (email ? 90 : 0) + 2490}">🌐  ${escape(web)}</text>
  </g>

  ${address ? `
    <text x="${TS_W / 2}" y="2900" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
          font-size="40" fill="#65718A">📍  ${escape(address)}</text>
  ` : ""}

  <!-- Footer-Banner -->
  <rect x="0" y="3260" width="${TS_W}" height="248" fill="url(#tsg)"/>
  <text x="${TS_W / 2}" y="3400" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="700" font-size="44" fill="#fff" letter-spacing="4">WWW.EASYGUTACHTER.DE</text>
</svg>`.trim();

export const TUERSCHILD_TEMPLATES = [
  { id: "tuerschild-a4", label: "Türschild DIN A4 (Hochformat)", build: buildTuerschild, w: TS_W, h: TS_H },
];


// ===================================================================
//   DIN-lang Wickelfalz-Flyer — Querformat 297 × 210 mm gefaltet auf
//   3 × 99 × 210 mm Panele.  @ 300 DPI = 3508 × 2480 px.
//   Wir liefern zwei SVGs: AUSSEN (Cover + Back-Panel + Innenklappe)
//                          INNEN (3 Inhaltspanele).
//   Falzlinien sind als gestrichelte Hilfslinien sichtbar.
// ===================================================================

const DL_W = 3508;     // 297 mm @ 300 DPI
const DL_H = 2480;     // 210 mm @ 300 DPI
const DL_PANEL = DL_W / 3;   // 99 mm @ 300 DPI ≈ 1169 px

const dlFold = `<g stroke="#cbd5e1" stroke-width="2" stroke-dasharray="14 10" opacity="0.9">
  <line x1="${DL_PANEL}" y1="0" x2="${DL_PANEL}" y2="${DL_H}"/>
  <line x1="${DL_PANEL * 2}" y1="0" x2="${DL_PANEL * 2}" y2="${DL_H}"/>
</g>`;

/**
 * AUSSEN-Seite (mit Wickelfalz):
 *   Panel 1 (links, schmaler bei korrektem Wickelfalz, aber wir bleiben bei 1/3): Adresse / Kontakt
 *   Panel 2 (Mitte): Leistungen
 *   Panel 3 (rechts): COVER mit „UNFALL GEHABT?"
 */
const buildDinLangAussen = ({ name = "Max Mustermann", phone = "", email = "",
                              web = "www.easygutachter.de", address = "" }) => `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${DL_W} ${DL_H}" preserveAspectRatio="xMidYMid meet" style="width:100%;height:100%;display:block">
  <defs>
    <linearGradient id="dlg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#06B6D4"/>
      <stop offset="100%" stop-color="#10B981"/>
    </linearGradient>
    <linearGradient id="dlgv" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#06B6D4"/>
      <stop offset="100%" stop-color="#10B981"/>
    </linearGradient>
  </defs>

  <rect width="${DL_W}" height="${DL_H}" fill="#ffffff"/>

  <!-- ============ Panel 3 (rechts) : COVER  ============ -->
  <rect x="${DL_PANEL * 2}" y="0" width="${DL_PANEL}" height="${DL_H}" fill="url(#dlg)"/>
  <circle cx="${DL_PANEL * 2 + 880}" cy="200" r="320" fill="#fff" opacity="0.07"/>
  <circle cx="${DL_PANEL * 2 + 100}" cy="2200" r="240" fill="#fff" opacity="0.07"/>

  <g transform="translate(${DL_PANEL * 2 + 80}, 140)">
    <image href="/easy-gutachter-logo.png" x="0" y="0" width="220" height="220" preserveAspectRatio="xMidYMid meet"/>
  </g>

  <text x="${DL_PANEL * 2 + 80}" y="660" font-family="Arial, Helvetica, sans-serif"
        font-weight="600" font-size="30" fill="#fff" opacity="0.92" letter-spacing="3">EASY GUTACHTER · PARTNER</text>

  <text x="${DL_PANEL * 2 + 80}" y="880" font-family="Arial, Helvetica, sans-serif"
        font-weight="800" font-size="120" fill="#fff" letter-spacing="-2">UNFALL</text>
  <text x="${DL_PANEL * 2 + 80}" y="1000" font-family="Arial, Helvetica, sans-serif"
        font-weight="800" font-size="120" fill="#fff" letter-spacing="-2">GEHABT?</text>
  <text x="${DL_PANEL * 2 + 80}" y="1110" font-family="Arial, Helvetica, sans-serif"
        font-weight="700" font-size="60" fill="#fff" opacity="0.95">Wir sind für Sie da.</text>

  <line x1="${DL_PANEL * 2 + 80}" y1="1200" x2="${DL_PANEL * 2 + 280}" y2="1200"
        stroke="#fff" stroke-width="6" opacity="0.9"/>

  <text x="${DL_PANEL * 2 + 80}" y="1320" font-family="Arial, Helvetica, sans-serif"
        font-weight="500" font-size="38" fill="#fff" opacity="0.95">Ihr unabhängiger</text>
  <text x="${DL_PANEL * 2 + 80}" y="1380" font-family="Arial, Helvetica, sans-serif"
        font-weight="500" font-size="38" fill="#fff" opacity="0.95">Kfz-Sachverständiger.</text>

  <text x="${DL_PANEL * 2 + 80}" y="2050" font-family="Arial, Helvetica, sans-serif"
        font-weight="800" font-size="46" fill="#fff">${escape(name)}</text>
  ${phone ? `<text x="${DL_PANEL * 2 + 80}" y="2120" font-family="Arial, Helvetica, sans-serif"
        font-weight="600" font-size="34" fill="#fff" opacity="0.9">📞 ${escape(phone)}</text>` : ""}
  ${email ? `<text x="${DL_PANEL * 2 + 80}" y="2180" font-family="Arial, Helvetica, sans-serif"
        font-weight="500" font-size="32" fill="#fff" opacity="0.9">✉ ${escape(email)}</text>` : ""}
  <text x="${DL_PANEL * 2 + 80}" y="2350" font-family="Arial, Helvetica, sans-serif"
        font-weight="600" font-size="28" fill="#fff" opacity="0.85" letter-spacing="2">${escape(web)}</text>

  <!-- ============ Panel 2 (Mitte) : Leistungen ============ -->
  <text x="${DL_PANEL + 80}" y="220" font-family="Arial, Helvetica, sans-serif"
        font-weight="800" font-size="44" fill="#06B6D4" letter-spacing="3">UNSERE LEISTUNGEN</text>
  <text x="${DL_PANEL + 80}" y="290" font-family="Arial, Helvetica, sans-serif"
        font-weight="500" font-size="28" fill="#65718A">Alles aus einer Hand</text>

  ${[
    ["Haftpflichtgutachten", "Bei unverschuldetem Unfall — meist kostenlos für Sie."],
    ["Kostenvoranschlag", "Schnelle Reparaturkosten-Schätzung mit Foto-Doku."],
    ["Wertgutachten", "Marktwert für Verkauf, Versicherung oder Erbschaft."],
    ["Beweissicherung", "Lückenlose Dokumentation für Anwalt und Gericht."],
    ["Schadenabwicklung", "Auf Wunsch komplett über uns — inkl. Rechtsanwalt."],
  ].map(([title, subtitle], i) => `
    <g transform="translate(${DL_PANEL + 80}, ${440 + i * 320})">
      <circle cx="40" cy="40" r="40" fill="url(#dlgv)"/>
      <text x="40" y="60" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
            font-weight="900" font-size="42" fill="#fff">${i + 1}</text>
      <text x="120" y="60" font-family="Arial, Helvetica, sans-serif"
            font-weight="800" font-size="40" fill="#0F172A">${escape(title)}</text>
      <text x="120" y="120" font-family="Arial, Helvetica, sans-serif"
            font-weight="500" font-size="28" fill="#65718A">${escape(subtitle)}</text>
    </g>
  `).join("")}

  <!-- ============ Panel 1 (links) : Rückseite mit Adresse  ============ -->
  <rect x="0" y="0" width="${DL_PANEL}" height="${DL_H}" fill="#F8FAFC"/>
  <rect x="0" y="0" width="${DL_PANEL}" height="180" fill="url(#dlg)"/>
  <text x="${DL_PANEL / 2}" y="115" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="800" font-size="38" fill="#fff" letter-spacing="3">KONTAKT &amp; ADRESSE</text>

  <g transform="translate(${DL_PANEL / 2 - 130}, 240)">
    <image href="/easy-gutachter-logo.png" x="0" y="0" width="260" height="260" preserveAspectRatio="xMidYMid meet"/>
  </g>

  <text x="${DL_PANEL / 2}" y="640" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="800" font-size="48" fill="#0F172A">${escape(name)}</text>
  <text x="${DL_PANEL / 2}" y="700" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="500" font-size="30" fill="#06B6D4">Kfz-Sachverständiger</text>

  <line x1="${DL_PANEL / 2 - 100}" y1="780" x2="${DL_PANEL / 2 + 100}" y2="780"
        stroke="url(#dlg)" stroke-width="6" stroke-linecap="round"/>

  <g font-family="Arial, Helvetica, sans-serif" font-size="34" fill="#0F172A" text-anchor="middle">
    ${phone ? `<text x="${DL_PANEL / 2}" y="900">📞  ${escape(phone)}</text>` : ""}
    ${email ? `<text x="${DL_PANEL / 2}" y="${phone ? 980 : 900}">✉  ${escape(email)}</text>` : ""}
    <text x="${DL_PANEL / 2}" y="${(phone ? 80 : 0) + (email ? 80 : 0) + 900}">🌐  ${escape(web)}</text>
  </g>

  ${address ? `
    <rect x="80" y="1300" width="${DL_PANEL - 160}" height="300" rx="22" fill="#fff" stroke="#E2E8F0" stroke-width="2"/>
    <text x="${DL_PANEL / 2}" y="1390" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
          font-weight="800" font-size="32" fill="#06B6D4" letter-spacing="2">📍  ADRESSE</text>
    <text x="${DL_PANEL / 2}" y="1480" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
          font-size="32" fill="#0F172A">${escape(address)}</text>
  ` : ""}

  <!-- QR-Platzhalter -->
  <rect x="${DL_PANEL / 2 - 150}" y="1780" width="300" height="300" rx="20" fill="#fff" stroke="#0F172A" stroke-width="3"/>
  <text x="${DL_PANEL / 2}" y="1940" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="700" font-size="28" fill="#65718A">QR-Code</text>
  <text x="${DL_PANEL / 2}" y="1980" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="500" font-size="20" fill="#94A3B8">(Webseite/Kontakt)</text>

  <text x="${DL_PANEL / 2}" y="2350" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="600" font-size="22" fill="#94A3B8" letter-spacing="2">EASY GUTACHTER · PARTNER</text>

  ${dlFold}
</svg>`.trim();

/**
 * INNEN-Seite (3 Inhalts-Panele):
 *   Panel 1: WAS TUN BEI EINEM UNFALL (5 Schritte)
 *   Panel 2: WARUM EIN GUTACHTER (Vorteile-Liste)
 *   Panel 3: JETZT KONTAKTIEREN (CTA)
 */
const buildDinLangInnen = ({ name = "Max Mustermann", phone = "", email = "",
                             web = "www.easygutachter.de" }) => `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${DL_W} ${DL_H}" preserveAspectRatio="xMidYMid meet" style="width:100%;height:100%;display:block">
  <defs>
    <linearGradient id="dlig" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#06B6D4"/>
      <stop offset="100%" stop-color="#10B981"/>
    </linearGradient>
    <linearGradient id="dligv" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#06B6D4"/>
      <stop offset="100%" stop-color="#10B981"/>
    </linearGradient>
  </defs>

  <rect width="${DL_W}" height="${DL_H}" fill="#ffffff"/>

  <!-- ============ Panel 1 : WAS TUN BEI EINEM UNFALL ============ -->
  <text x="80" y="220" font-family="Arial, Helvetica, sans-serif"
        font-weight="800" font-size="44" fill="#06B6D4" letter-spacing="3">WAS TUN BEI EINEM UNFALL?</text>
  <text x="80" y="290" font-family="Arial, Helvetica, sans-serif"
        font-weight="500" font-size="28" fill="#65718A">5 Schritte — kurz erklärt</text>

  ${[
    ["Unfallstelle sichern", "Warnblinker, Warnweste, Warndreieck — Sicherheit zuerst."],
    ["Polizei rufen", "Vor allem bei Personenschaden, Streit oder hohem Sachschaden."],
    ["Daten austauschen", "Name, Adresse, Versicherung & Kennzeichen aller Beteiligten."],
    ["Beweise sichern", "Fotos vom Schaden, Skizze, Zeugen — alles dokumentieren."],
    ["Gutachter rufen", "Ihr eigener Gutachter — keiner von der Versicherung."],
  ].map(([title, subtitle], i) => `
    <g transform="translate(80, ${440 + i * 320})">
      <circle cx="40" cy="40" r="40" fill="url(#dligv)"/>
      <text x="40" y="60" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
            font-weight="900" font-size="42" fill="#fff">${i + 1}</text>
      <text x="120" y="60" font-family="Arial, Helvetica, sans-serif"
            font-weight="800" font-size="40" fill="#0F172A">${escape(title)}</text>
      <text x="120" y="120" font-family="Arial, Helvetica, sans-serif"
            font-weight="500" font-size="28" fill="#65718A">${escape(subtitle)}</text>
    </g>
  `).join("")}

  <!-- ============ Panel 2 : VORTEILE ============ -->
  <text x="${DL_PANEL + 80}" y="220" font-family="Arial, Helvetica, sans-serif"
        font-weight="800" font-size="44" fill="#06B6D4" letter-spacing="3">IHRE VORTEILE</text>
  <text x="${DL_PANEL + 80}" y="290" font-family="Arial, Helvetica, sans-serif"
        font-weight="500" font-size="28" fill="#65718A">Warum Sie zu uns kommen sollten</text>

  ${[
    "Kostenlos bei unverschuldetem Unfall",
    "Schnelle Termine — oft am selben Tag",
    "Vor-Ort-Service: zuhause, Werkstatt, Büro",
    "100 % unabhängig von Versicherungen",
    "Komplette Schadenabwicklung möglich",
    "Gutachten in 24 Stunden",
    "Digital — keine Papierberge",
    "Anwalt-Empfehlung auf Wunsch",
  ].map((line, i) => `
    <g transform="translate(${DL_PANEL + 80}, ${440 + i * 195})">
      <circle cx="36" cy="36" r="36" fill="#10B981"/>
      <path d="M 16 38 L 30 50 L 56 22" fill="none" stroke="#fff" stroke-width="8"
            stroke-linecap="round" stroke-linejoin="round"/>
      <text x="100" y="50" font-family="Arial, Helvetica, sans-serif"
            font-weight="600" font-size="34" fill="#0F172A">${escape(line)}</text>
    </g>
  `).join("")}

  <!-- ============ Panel 3 : JETZT KONTAKTIEREN ============ -->
  <rect x="${DL_PANEL * 2}" y="0" width="${DL_PANEL}" height="${DL_H}" fill="url(#dlig)"/>
  <circle cx="${DL_PANEL * 2 + 1000}" cy="2300" r="320" fill="#fff" opacity="0.06"/>

  <text x="${DL_PANEL * 2 + 80}" y="220" font-family="Arial, Helvetica, sans-serif"
        font-weight="700" font-size="40" fill="#fff" opacity="0.85" letter-spacing="3">RUFEN SIE UNS AN</text>

  <text x="${DL_PANEL * 2 + 80}" y="380" font-family="Arial, Helvetica, sans-serif"
        font-weight="900" font-size="100" fill="#fff" letter-spacing="-2">JETZT</text>
  <text x="${DL_PANEL * 2 + 80}" y="490" font-family="Arial, Helvetica, sans-serif"
        font-weight="900" font-size="100" fill="#fff" letter-spacing="-2">KONTAKT</text>
  <text x="${DL_PANEL * 2 + 80}" y="600" font-family="Arial, Helvetica, sans-serif"
        font-weight="900" font-size="100" fill="#fff" letter-spacing="-2">AUFNEHMEN!</text>

  <line x1="${DL_PANEL * 2 + 80}" y1="700" x2="${DL_PANEL * 2 + 280}" y2="700"
        stroke="#fff" stroke-width="6" opacity="0.9"/>

  <!-- Kontakt-Cards -->
  ${phone ? `
    <rect x="${DL_PANEL * 2 + 80}" y="850" width="${DL_PANEL - 160}" height="220" rx="22" fill="rgba(255,255,255,0.18)"/>
    <text x="${DL_PANEL * 2 + 130}" y="935" font-family="Arial, Helvetica, sans-serif"
          font-weight="600" font-size="32" fill="#fff" opacity="0.85">📞 Telefon</text>
    <text x="${DL_PANEL * 2 + 130}" y="1020" font-family="Arial, Helvetica, sans-serif"
          font-weight="800" font-size="56" fill="#fff">${escape(phone)}</text>
  ` : ""}

  ${email ? `
    <rect x="${DL_PANEL * 2 + 80}" y="${phone ? 1130 : 850}" width="${DL_PANEL - 160}" height="220" rx="22" fill="rgba(255,255,255,0.18)"/>
    <text x="${DL_PANEL * 2 + 130}" y="${phone ? 1215 : 935}" font-family="Arial, Helvetica, sans-serif"
          font-weight="600" font-size="32" fill="#fff" opacity="0.85">✉ E-Mail</text>
    <text x="${DL_PANEL * 2 + 130}" y="${phone ? 1300 : 1020}" font-family="Arial, Helvetica, sans-serif"
          font-weight="800" font-size="44" fill="#fff">${escape(email)}</text>
  ` : ""}

  <!-- CTA Footer-Block -->
  <text x="${DL_PANEL * 2 + DL_PANEL / 2}" y="2150" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="800" font-size="44" fill="#fff" opacity="0.9">${escape(name)}</text>
  <text x="${DL_PANEL * 2 + DL_PANEL / 2}" y="2240" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="500" font-size="32" fill="#fff" opacity="0.8">Easy Gutachter Partner</text>
  <text x="${DL_PANEL * 2 + DL_PANEL / 2}" y="2400" text-anchor="middle" font-family="Arial, Helvetica, sans-serif"
        font-weight="600" font-size="28" fill="#fff" opacity="0.85" letter-spacing="3">${escape(web)}</text>

  ${dlFold}
</svg>`.trim();

export const DINLANG_TEMPLATES = [
  { id: "dl-aussen", label: "DIN-lang AUSSEN (Cover)",  build: buildDinLangAussen, w: DL_W, h: DL_H },
  { id: "dl-innen",  label: "DIN-lang INNEN (Inhalt)",  build: buildDinLangInnen,  w: DL_W, h: DL_H },
];


// ===================================================================
//   Helper: build EasyGutachter-style email "vorname.nachname@easygutachter.de"
// ===================================================================

export function easyGutachterEmail(fullName = "") {
  if (!fullName) return "";
  const slug = String(fullName)
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")  // strip accents
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9\s]/g, "")
    .trim()
    .split(/\s+/)
    .join(".");
  if (!slug) return "";
  return `${slug}@easygutachter.de`;
}
